import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api/client";
import { devLog } from "@/lib/devLog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Camera, MapPin, Clock, CheckCircle2, LogIn, LogOut, RotateCcw, Loader2 } from "lucide-react";

const PING_INTERVAL_MS = 60_000;

async function getGeo() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("Geolocation not supported by this browser."));
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy_m: p.coords.accuracy }),
      (e) => reject(new Error(e.message || "Location permission denied")),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 5000 },
    );
  });
}

export default function DriverAttendance() {
  const qc = useQueryClient();
  const { data: today, isLoading } = useQuery({
    queryKey: ["attendance-today"],
    queryFn: () => api.get("/attendance/today").then((r) => r.data),
    refetchInterval: 30_000,
  });

  const [photo, setPhoto] = useState(null);            // { blob, dataUrl }
  const [note, setNote] = useState("");
  const [coords, setCoords] = useState(null);
  const [pinging, setPinging] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraOpen, setCameraOpen] = useState(false);

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCameraOpen(false);
  };

  const openCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
      streamRef.current = stream;
      setCameraOpen(true);
      requestAnimationFrame(() => {
        if (videoRef.current) videoRef.current.srcObject = stream;
      });
    } catch (e) {
      toast.error(`Camera error: ${e.message || e}`);
    }
  };

  const snapPhoto = () => {
    const v = videoRef.current;
    if (!v) return;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth || 480;
    canvas.height = v.videoHeight || 640;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      const dataUrl = canvas.toDataURL("image/jpeg", 0.75);
      setPhoto({ blob, dataUrl });
      stopCamera();
    }, "image/jpeg", 0.75);
  };

  const refreshCoords = async () => {
    try {
      const c = await getGeo();
      setCoords(c);
      toast.success(`Location captured (±${Math.round(c.accuracy_m)}m)`);
    } catch (e) { toast.error(e.message); }
  };

  const checkIn = useMutation({
    mutationFn: () => api.post("/attendance/check-in", {
      lat: coords.lat, lng: coords.lng, accuracy_m: coords.accuracy_m,
      photo_base64: photo.dataUrl, note,
    }),
    onSuccess: () => {
      toast.success("Checked in — shift started");
      setPhoto(null); setNote("");
      qc.invalidateQueries({ queryKey: ["attendance-today"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Check-in failed"),
  });

  const checkOut = useMutation({
    mutationFn: () => api.post("/attendance/check-out", {
      lat: coords.lat, lng: coords.lng, accuracy_m: coords.accuracy_m,
      photo_base64: photo?.dataUrl || "", note,
    }),
    onSuccess: () => {
      toast.success("Checked out — thanks for today!");
      setPhoto(null); setNote("");
      qc.invalidateQueries({ queryKey: ["attendance-today"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Check-out failed"),
  });

  // Auto-ping location every 60 s while on shift
  const isOnShift = today?.status === "on_shift";
  useEffect(() => {
    if (!isOnShift) return undefined;
    let cancelled = false;
    const ping = async () => {
      if (cancelled) return;
      try {
        const c = await getGeo();
        setPinging(true);
        await api.post("/attendance/location", c).catch((e) => devLog.debug("[attendance] location ping dropped:", e?.message || e));
      } catch (e) {
        // Geolocation permission may flap intermittently (user switched apps, briefly denied, etc.).
        // Log for debugging but don't spam the driver with toasts — next tick will retry.
        devLog.debug("[attendance] geo pull skipped:", e?.message || e);
      }
      finally { if (!cancelled) setPinging(false); }
    };
    ping();
    const t = setInterval(ping, PING_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, [isOnShift]);

  useEffect(() => () => stopCamera(), []);

  const status = today?.status || "not_started";

  return (
    <div className="mx-auto max-w-xl space-y-4 p-4" data-testid="driver-attendance-page">
      <div className="flex items-center gap-2">
        <Clock size={22} className="text-primary" />
        <h1 className="font-display text-2xl font-black tracking-tight">My attendance</h1>
      </div>

      <StatusCard today={today} isLoading={isLoading} pinging={pinging} isOnShift={isOnShift} />

      {status !== "completed" && (
        <Card className="border-primary/30">
          <CardContent className="space-y-4 p-4">
            <div className="text-sm font-bold uppercase tracking-widest text-primary">
              {status === "not_started" ? "Ready to check in" : "Ready to check out"}
            </div>

            {/* Photo */}
            <div>
              <div className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Selfie photo</div>
              {photo ? (
                <div className="relative overflow-hidden rounded-lg border border-border">
                  <img src={photo.dataUrl} alt="captured" className="w-full" />
                  <Button variant="outline" size="sm" className="absolute right-2 top-2" onClick={() => setPhoto(null)} data-testid="attendance-photo-retake">
                    <RotateCcw size={14} className="mr-1" /> Retake
                  </Button>
                </div>
              ) : cameraOpen ? (
                <div className="space-y-2">
                  <video ref={videoRef} autoPlay playsInline muted className="w-full rounded-lg border border-border" />
                  <div className="flex gap-2">
                    <Button className="flex-1" onClick={snapPhoto} data-testid="attendance-photo-snap"><Camera size={14} className="mr-1" /> Snap</Button>
                    <Button variant="outline" onClick={stopCamera}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <Button variant="outline" className="w-full" onClick={openCamera} data-testid="attendance-open-camera">
                  <Camera size={14} className="mr-1" /> Open camera
                </Button>
              )}
            </div>

            {/* Coordinates */}
            <div>
              <div className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">GPS location</div>
              {coords ? (
                <div className="rounded-lg border border-border bg-muted/30 p-3 font-mono text-xs">
                  <div>lat {coords.lat.toFixed(6)}</div>
                  <div>lng {coords.lng.toFixed(6)}</div>
                  <div className="text-muted-foreground">±{Math.round(coords.accuracy_m || 0)} m</div>
                  <button className="mt-2 text-[11px] font-semibold text-primary underline" onClick={refreshCoords}>Refresh</button>
                </div>
              ) : (
                <Button variant="outline" className="w-full" onClick={refreshCoords} data-testid="attendance-capture-gps">
                  <MapPin size={14} className="mr-1" /> Capture GPS
                </Button>
              )}
            </div>

            {/* Note */}
            <div>
              <div className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Note (optional)</div>
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything to flag today?" />
            </div>

            {/* Submit */}
            {status === "not_started" ? (
              <Button
                size="lg"
                className="w-full"
                disabled={!photo || !coords || checkIn.isPending}
                onClick={() => checkIn.mutate()}
                data-testid="attendance-checkin-btn"
              >
                {checkIn.isPending ? <Loader2 size={16} className="mr-2 animate-spin" /> : <LogIn size={16} className="mr-2" />}
                Check in
              </Button>
            ) : (
              <Button
                size="lg"
                variant="destructive"
                className="w-full"
                disabled={!coords || checkOut.isPending}
                onClick={() => checkOut.mutate()}
                data-testid="attendance-checkout-btn"
              >
                {checkOut.isPending ? <Loader2 size={16} className="mr-2 animate-spin" /> : <LogOut size={16} className="mr-2" />}
                Check out
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {status === "completed" && (
        <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-4 text-sm text-emerald-800 dark:text-emerald-200">
          <div className="flex items-center gap-2 font-semibold"><CheckCircle2 size={16} /> Shift completed for today</div>
          <div className="mt-1 text-xs opacity-80">Total worked: {today.hours_worked} hours</div>
        </div>
      )}
    </div>
  );
}

function StatusCard({ today, isLoading, pinging, isOnShift }) {
  if (isLoading) return <div className="rounded-lg border border-border p-4 text-sm text-muted-foreground">Loading today…</div>;
  const status = today?.status || "not_started";
  const badge = {
    not_started: { text: "Not started", cls: "bg-slate-200 text-slate-800" },
    on_shift:    { text: "ON SHIFT",    cls: "bg-emerald-500 text-white" },
    completed:   { text: "COMPLETED",   cls: "bg-blue-500 text-white" },
  }[status] || { text: status, cls: "bg-slate-200 text-slate-800" };
  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Today · {today?.date}</div>
          <span className={`rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${badge.cls}`} data-testid="attendance-status">
            {badge.text}
          </span>
        </div>
        {today?.check_in_at && (
          <div className="text-xs">Checked in at <b>{new Date(today.check_in_at).toLocaleTimeString()}</b></div>
        )}
        {today?.check_out_at && (
          <div className="text-xs">Checked out at <b>{new Date(today.check_out_at).toLocaleTimeString()}</b> · {today?.hours_worked} h</div>
        )}
        {isOnShift && (
          <div className="text-[11px] text-muted-foreground">
            {pinging ? "Sharing live location…" : "Live location is being shared with dispatch every 60 s"}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
